import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  IconButton,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  FormControlLabel,
  Switch,
  Autocomplete,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import firebaseDataService from '../../../services/FirebaseDataService';
import { getCurrencySettings } from '../../../services/migration/companyDataService';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import GuideButton from '../../common/guide/GuideButton';

// Props interface
interface AddCreditCardModalProps {
  open: boolean;
  onClose: () => void;
  bankAccountId?: number; // Bağlı banka hesabı ID'si (opsiyonel)
  bankAccountName?: string; // Banka adı (görüntüleme için)
  cardToEdit?: CreditCardData | null; // Düzenlenecek kart
  /** Şube yöneticisi: kart bu şubeye sabitlenir ve "Sadece Bu Şube" zorunlu açık olur. */
  lockedBranchId?: string | null;
}

// Kredi kartı veri yapısı
interface CreditCardData {
  id?: number;
  companyId?: string;
  bankAccountId?: number | null;
  branchId?: string | null;
  accountName: string;
  description?: string;
  cardNumber: string;
  statementDay: number;
  dueDay: number;
  creditLimit: number;
  minPaymentAmount: number;
  usedAmount?: number;
  currency: string;
  isActive: boolean;
  isBranchOnly: boolean;
  notes?: string;
}

// Şube arayüzü
interface Branch {
  id: string;
  name: string;
}

const AddCreditCardModal: React.FC<AddCreditCardModalProps> = ({
  open,
  onClose,
  bankAccountId,
  bankAccountName,
  cardToEdit,
  lockedBranchId = null,
}) => {
  const { i18n } = useTranslation('accounting');
  const isTr = i18n.language?.toLowerCase().startsWith('tr');
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);

  // AuthContext'ten şubeleri al
  const { branches } = useAuth();
  
  // Düzenleme modu kontrolü
  const isEditMode = !!cardToEdit;
  
  // Form state
  const [accountName, setAccountName] = useState('');
  const [description, setDescription] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [statementDay, setStatementDay] = useState(1);
  const [dueDay, setDueDay] = useState(10);
  const [creditLimit, setCreditLimit] = useState('');
  const [minPaymentAmount, setMinPaymentAmount] = useState('');
  const [currency, setCurrency] = useState('TRY');
  const [isActive, setIsActive] = useState(true);
  const [isBranchOnly, setIsBranchOnly] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [notes, setNotes] = useState('');
  
  // Yükleme durumları
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  
  // Para birimi seçenekleri
  const [availableCurrencies, setAvailableCurrencies] = useState<{code: string, active: boolean}[]>([
    { code: 'TRY', active: true }
  ]);

  // Günler (1-31)
  const days = Array.from({ length: 31 }, (_, i) => i + 1);

  // Modal açıldığında
  useEffect(() => {
    if (open) {
      loadCompanyAndData();
      
      if (cardToEdit) {
        populateFormForEdit(cardToEdit);
      } else {
        resetForm();
      }
    }
  }, [open, cardToEdit]);

  const loadCompanyAndData = async () => {
    try {
      setInitialLoading(true);
      
      const uid = ownAuthSession.getCurrentUid();
      if (!uid) {
        setInitialLoading(false);
        setError(tt('Kullanıcı oturumu açık değil.', 'User session is not active.'));
        return;
      }
      
      const userData = await firebaseDataService.getUserData(uid);
      
      if (!userData) {
        setInitialLoading(false);
        setError(tt('Kullanıcı bilgileri bulunamadı.', 'User information not found.'));
        return;
      }
      
      const companyId = userData.companyId;
      setCompanyId(companyId || null);
      
      if (!companyId) {
        setInitialLoading(false);
        setError(tt('Şirket bilgisi bulunamadı.', 'Company information not found.'));
        return;
      }
      
      // Para birimlerini yükle
      await loadCurrencies(companyId);
      
    } catch (error) {
      console.error('Veri yüklenirken hata:', error);
      setInitialLoading(false);
      setError(tt('Veriler yüklenirken bir hata oluştu.', 'An error occurred while loading data.'));
    }
  };

  // Düzenleme için formu doldur
  const populateFormForEdit = (card: CreditCardData) => {
    console.log('📝 Düzenleme modu: Form dolduruluyor...', card);
    setAccountName(card.accountName || '');
    setDescription(card.description || '');
    setCardNumber(card.cardNumber || '');
    setStatementDay(card.statementDay || 1);
    setDueDay(card.dueDay || 10);
    setCreditLimit(card.creditLimit?.toString() || '');
    setMinPaymentAmount(card.minPaymentAmount?.toString() || '');
    setCurrency(card.currency || 'TRY');
    setIsActive(card.isActive !== false);
    setIsBranchOnly(card.isBranchOnly || false);
    setNotes(card.notes || '');
    
    // Şube seçimi
    if (card.branchId && branches.length > 0) {
      const branch = branches.find((b: Branch) => b.id === card.branchId);
      setSelectedBranch(branch || null);
    }
  };

  // Para birimlerini yükle
  const loadCurrencies = async (companyId: string) => {
    try {
      const currencyData = await getCurrencySettings(companyId);
      
      const currencies: {code: string, active: boolean}[] = [
        { code: 'TRY', active: true }
      ];
      
      if (currencyData) {
        Object.entries(currencyData).forEach(([code, active]: [string, any]) => {
          if (code !== 'TRY' && active === true) {
            currencies.push({ code, active: true });
          }
        });
      }
      
      setAvailableCurrencies(currencies);
      
      const currencyExists = currencies.some(c => c.code === currency && c.active);
      if (!currencyExists) {
        setCurrency('TRY');
      }
    } catch (error) {
      console.error('Para birimi yükleme hatası:', error);
      setAvailableCurrencies([{ code: 'TRY', active: true }]);
    } finally {
      setInitialLoading(false);
    }
  };

  // Form alanlarını kontrol et
  const isFormValid = () => {
    return accountName.trim() !== '' && 
           cardNumber.trim() !== '' && 
           creditLimit !== '' && 
           parseFloat(creditLimit) >= 0;
  };

  // Para birimi sembolü
  const getCurrencySymbol = (code: string): string => {
    switch(code) {
      case 'TRY': return '₺';
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      default: return code;
    }
  };

  // Kart numarası formatla (görüntüleme için)
  const formatCardNumber = (value: string): string => {
    // Sadece rakamları al
    const digits = value.replace(/\D/g, '');
    // 4'erli grupla
    const groups = digits.match(/.{1,4}/g);
    return groups ? groups.join(' ') : digits;
  };

  // Kart numarası input handler
  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ''); // Sadece rakamlar
    if (value.length <= 16) {
      setCardNumber(formatCardNumber(value));
    }
  };

  // Kaydetme işlemi
  const handleSave = async () => {
    if (!isFormValid()) {
      setError(tt('Lütfen zorunlu alanları doldurunuz (Hesap Adı, Kart No, Kart Limiti).', 'Please fill in required fields (Account Name, Card Number, Card Limit).'));
      return;
    }

    if (!companyId) {
      setError(tt('Şirket bilgisi bulunamadı.', 'Company information not found.'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const cardData = {
        CompanyId: companyId,
        BankAccountId: bankAccountId || null,
        BranchId: lockedBranchId || selectedBranch?.id || null,
        AccountName: accountName.trim(),
        Description: description.trim() || null,
        CardNumber: cardNumber.replace(/\s/g, ''), // Boşlukları kaldır
        StatementDay: statementDay,
        DueDay: dueDay,
        CreditLimit: parseFloat(creditLimit) || 0,
        MinPaymentAmount: parseFloat(minPaymentAmount) || 0,
        Currency: currency,
        IsActive: isActive,
        IsBranchOnly: lockedBranchId ? true : isBranchOnly,
        Notes: notes.trim() || null,
        CreatedBy: ownAuthSession.getCurrentEmail() || 'System',
        UpdatedBy: ownAuthSession.getCurrentEmail() || 'System'
      };

      if (isEditMode && cardToEdit?.id) {
        // Güncelleme
        console.log('✏️ Kredi kartı güncelleniyor...', cardData);
        
        await accountingApi.post(ACCOUNTING_API.CREDIT_CARD_UPDATE(cardToEdit.id), cardData);
        
        console.log('✅ Kredi kartı güncellendi');
      } else {
        // Yeni ekleme
        console.log('💳 Yeni kredi kartı oluşturuluyor...', cardData);
        
        await accountingApi.post(ACCOUNTING_API.CREDIT_CARDS, cardData);
        
        console.log('✅ Kredi kartı oluşturuldu');
      }
      
      resetForm();
      onClose();
    } catch (error: any) {
      console.error('❌ Kredi kartı kaydedilirken hata:', error);
      setError(`${tt('Kredi kartı kaydedilirken bir hata oluştu', 'An error occurred while saving credit card')}: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Form sıfırla
  const resetForm = () => {
    setAccountName('');
    setDescription('');
    setCardNumber('');
    setStatementDay(1);
    setDueDay(10);
    setCreditLimit('');
    setMinPaymentAmount('');
    setCurrency('TRY');
    setIsActive(true);
    if (lockedBranchId) {
      setIsBranchOnly(true);
      setSelectedBranch(branches.find((b: Branch) => b.id === lockedBranchId) || { id: lockedBranchId, name: lockedBranchId });
    } else {
      setIsBranchOnly(false);
      setSelectedBranch(null);
    }
    setNotes('');
    setError(null);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ 
        m: 0, 
        p: 2, 
        display: 'flex', 
        alignItems: 'center',
        bgcolor: 'primary.main',
        color: 'white'
      }}>
        <CreditCardIcon sx={{ mr: 1 }} />
        <Typography variant="h6" component="span">
          {isEditMode ? tt('Kredi Kartı Düzenle', 'Edit Credit Card') : tt('Yeni Kredi Kartı Ekle', 'Add New Credit Card')}
        </Typography>
        {bankAccountName && (
          <Typography variant="body2" sx={{ ml: 2, opacity: 0.9 }}>
            ({bankAccountName})
          </Typography>
        )}
        <GuideButton guideKey="finance.addCreditCard" size="small" color="inherit" sx={{ ml: 0.5 }} />
        <IconButton
          aria-label={tt('kapat', 'close')}
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {initialLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {error && (
              <Grid item xs={12}>
                <Alert severity="error" onClose={() => setError(null)}>
                  {error}
                </Alert>
              </Grid>
            )}

            {/* Hesap Adı */}
            <Grid item xs={12} sm={6}>
              <TextField
                label={tt('Hesap Adı *', 'Account Name *')}
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                fullWidth
                placeholder={tt('Örn: Şirket Kredi Kartı', 'E.g.: Company Credit Card')}
                helperText={tt('Kartı tanımlayan bir isim girin', 'Enter a name to identify the card')}
              />
            </Grid>

            {/* Kart Numarası */}
            <Grid item xs={12} sm={6}>
              <TextField
                label={tt('Kart Numarası *', 'Card Number *')}
                value={cardNumber}
                onChange={handleCardNumberChange}
                fullWidth
                placeholder="1234 5678 9012 3456"
                inputProps={{ maxLength: 19 }}
                helperText={tt('16 haneli kart numarası', '16-digit card number')}
              />
            </Grid>

            {/* Tanımı */}
            <Grid item xs={12}>
              <TextField
                label={tt('Tanımı / Açıklama', 'Description')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                multiline
                rows={2}
                placeholder={tt('Kart hakkında açıklama...', 'Description about the card...')}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {tt('Ödeme Bilgileri', 'Payment Information')}
                </Typography>
              </Divider>
            </Grid>

            {/* Hesap Kesim Günü */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>{tt('Hesap Kesim Günü', 'Statement Day')}</InputLabel>
                <Select
                  value={statementDay}
                  label={tt('Hesap Kesim Günü', 'Statement Day')}
                  onChange={(e) => setStatementDay(Number(e.target.value))}
                >
                  {days.map(day => (
                    <MenuItem key={day} value={day}>{day}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Son Ödeme Günü */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>{tt('Son Ödeme Günü', 'Due Day')}</InputLabel>
                <Select
                  value={dueDay}
                  label={tt('Son Ödeme Günü', 'Due Day')}
                  onChange={(e) => setDueDay(Number(e.target.value))}
                >
                  {days.map(day => (
                    <MenuItem key={day} value={day}>{day}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Kart Limiti */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label={tt('Kart Limiti *', 'Card Limit *')}
                value={creditLimit}
                onChange={(e) => setCreditLimit(e.target.value.replace(/[^0-9.]/g, ''))}
                fullWidth
                type="text"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {getCurrencySymbol(currency)}
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            {/* Min. Ödeme Tutarı */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label={tt('Min. Ödeme Tutarı', 'Min. Payment Amount')}
                value={minPaymentAmount}
                onChange={(e) => setMinPaymentAmount(e.target.value.replace(/[^0-9.]/g, ''))}
                fullWidth
                type="text"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {getCurrencySymbol(currency)}
                    </InputAdornment>
                  )
                }}
                helperText={tt('Boş bırakılırsa %20 hesaplanır', 'If empty, 20% is calculated')}
              />
            </Grid>

            {/* Para Birimi */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel>{tt('Para Birimi', 'Currency')}</InputLabel>
                <Select
                  value={currency}
                  label={tt('Para Birimi', 'Currency')}
                  onChange={(e) => setCurrency(e.target.value as string)}
                >
                  {availableCurrencies.filter(c => c.active).map(curr => (
                    <MenuItem key={curr.code} value={curr.code}>
                      {curr.code} ({getCurrencySymbol(curr.code)})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Şube Seçimi */}
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                options={branches}
                getOptionLabel={(option: Branch) => option.name}
                value={selectedBranch}
                onChange={(_, newValue) => setSelectedBranch(newValue)}
                disabled={!!lockedBranchId}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={tt('Şube', 'Branch')}
                    placeholder={tt('Şube seçin (opsiyonel)', 'Select branch (optional)')}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Grid>

            {/* Aktif/Pasif & Sadece Bu Şube */}
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      color="success"
                    />
                  }
                  label={isActive ? tt('Aktif', 'Active') : tt('Pasif', 'Passive')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={isBranchOnly}
                      onChange={(e) => setIsBranchOnly(e.target.checked)}
                      color="primary"
                      disabled={!selectedBranch || !!lockedBranchId}
                    />
                  }
                  label={tt('Sadece Bu Şube', 'Only This Branch')}
                />
              </Box>
            </Grid>

            {/* Ek Bilgi */}
            <Grid item xs={12}>
              <TextField
                label={tt('Ek Bilgi / Notlar', 'Additional Information / Notes')}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                fullWidth
                multiline
                rows={3}
                placeholder={tt('Ek notlar...', 'Additional notes...')}
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          disabled={loading}
        >
          {tt('İptal', 'Cancel')}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || initialLoading || !isFormValid()}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CreditCardIcon />}
        >
          {loading ? tt('Kaydediliyor...', 'Saving...') : (isEditMode ? tt('Güncelle', 'Update') : tt('Kaydet', 'Save'))}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCreditCardModal;
